import {attemptSessionId} from "./activity-continuity";
import {useEffect, useRef, useState} from "react";
import type {LibraryState} from "./activity-library";
import type {LiveUpdate} from "./activity-live";

export function useActivityLive(state: LibraryState | undefined, scope: {sessionId?: string; attemptId?: string}, onUpdate: (update: LiveUpdate) => void, enabled = true) {
  const current = useRef({state, onUpdate});
  current.current = {state, onUpdate};
  const [presence, setPresence] = useState<LiveUpdate["presence"]>([]);
  const [error, setError] = useState("");
  const {sessionId, attemptId} = scope;
  useEffect(() => {
    if (!enabled || (!sessionId && !attemptId)) return;
    let stopped = false, timer: ReturnType<typeof setTimeout>, etag = "";
    const controller = new AbortController();
    async function poll() {
      try {
        if (document.visibilityState !== "visible") return;
        const attempts = current.current.state?.attempts.filter(a => sessionId ? attemptSessionId(a) === sessionId : a.id === attemptId) ?? [];
        const query = new URLSearchParams({...(sessionId ? {sessionId} : {attemptId: attemptId!}), known: JSON.stringify(Object.fromEntries(attempts.map(a => [a.id, a.revision])))});
        const response = await fetch(`/api/activity-library/live?${query}`, {credentials: "include", signal: controller.signal, headers: etag ? {"If-None-Match": etag} : {}});
        if (response.status === 304) {setError(""); return;}
        if (!response.ok) throw new Error("Suivi interrompu — nouvelle tentative automatique.");
        const update: LiveUpdate = await response.json();
        if (stopped) return;
        etag = response.headers.get("etag") ?? "";
        current.current.onUpdate(update);
        setPresence(previous => JSON.stringify(previous) === JSON.stringify(update.presence) ? previous : update.presence);
        setError("");
      } catch (e) {if (!stopped) setError(e instanceof Error ? e.message : "Suivi indisponible.");}
      finally {if (!stopped) timer = setTimeout(() => void poll(), 3000);}
    }
    void poll();
    return () => {stopped = true; clearTimeout(timer); controller.abort();};
  }, [sessionId, attemptId, enabled]);
  return {presence, error};
}
